// ProfilePage.jsx
import React, { useState, useEffect } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import profileImg from "../image/profile.webp";
import axios from "axios";
import { BASE_URL } from "../Apis/apiConfig";
import { User, Mail, Phone, Building2, Users, MapPin, Calendar, Copy, Eye, X, CheckCircle } from "lucide-react";
import "../Style/profile.css";

const ProfilePage = () => {
  const [userData, setUserData] = useState(null);
  const [role, setRole] = useState(null);
  const [error, setError] = useState("");
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingRole, setLoadingRole] = useState(false);
  const [showSocietyModal, setShowSocietyModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const [societyData, setSocietyData] = useState(null);
  const [loadingSociety, setLoadingSociety] = useState(false);

  const getLS = (k) => {
    const v = localStorage.getItem(k);
    return v ? v.trim() : "";
  };

  useEffect(() => {
    const fetchProfile = async () => {
      setError("");
      setLoadingProfile(true);

      const token = getLS("token");
      const email = getLS("email");
      const societyName = getLS("society");

      if (!token || !email) {
        setError("User not authenticated");
        setUserData(null);
        setLoadingProfile(false);
        return;
      }
      if (!societyName) {
        setError("No society selected");
        setUserData(null);
        setLoadingProfile(false);
        return;
      }

      try {
        const response = await axios.get(`${BASE_URL}/api/get-login-admin`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { adminEmail: email, societyName },
          validateStatus: (s) => s < 500,
        });

        if (response.status === 401) {
          setError("Unauthorized — please log in again");
          setUserData(null);
          return;
        }
        if (response.status >= 400) {
          setError(`Failed to fetch profile: ${response.status}`);
          console.error("Profile fetch failed:", response.status, response.data);
          setUserData(null);
          return;
        }

        const data =
          Array.isArray(response.data) && response.data.length > 0
            ? response.data[0]
            : response.data;
        setUserData(data || null);
        console.log("Admin profile fetched:", data);
      } catch (err) {
        console.error("Error fetching profile:", err);
        setError("Failed to fetch profile");
        setUserData(null);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, []);

 
  const getSocietyId = () => {
   
    if (societyData) {
      return (
        societyData.society_registration_no ??
        societyData.societyRegistrationNo ??
        societyData.societyRegistration ??
        societyData.society_reg_no ??
        "N/A"
      );
    }
    if (userData) {
      return (
        userData.society_registration_no ??
        userData.societyRegistrationNo ??
        userData.society_registration_no ??
        userData.society_reg_no ??
        "N/A"
      );
    }
    return getLS("societyRegNo") || getLS("societyId") || "N/A";
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.error("Copy failed:", err);
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } catch {
        setCopied(false);
        alert("Unable to copy. Please copy manually.");
      }
      document.body.removeChild(ta);
    }
  };

  // Fetch society by admin (using your POST with @RequestParam style)
  const fetchSocietyByAdmin = async () => {
    setLoadingSociety(true);
    setError("");

    const token = (localStorage.getItem("token") || "").trim();
    const societyName = (localStorage.getItem("society") || "").trim();
    const registered = (localStorage.getItem("societyRegNo") || "").trim();

    // If you prefer to use data from userData (when it's fetched), you can:
    // const societyName = userData?.society ?? getLS("society")
    // const registered = getLS("societyRegNo") // or userData?.society_registration_no

    if (!token) {
      setError("Missing authentication token. Please log in.");
      setLoadingSociety(false);
      return;
    }
    if (!societyName) {
      setError("Missing society name in localStorage.");
      setLoadingSociety(false);
      return;
    }
    if (!registered) {
      setError("Missing society registration number in localStorage.");
      setLoadingSociety(false);
      return;
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/api/get-society-by-admin`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            societyName: societyName,
            societyRegistrationNo: registered,
          },
          validateStatus: (s) => s < 500,
        }
      );

      if (response.status === 401) {
        setError("Unauthorized — please log in again");
        setSocietyData(null);
        return;
      }

      if (response.status >= 400) {
        console.error("Society fetch failed:", response.status, response.data);
        setError(`Failed to fetch society: ${response.status}`);
        setSocietyData(null);
        return;
      }

      const data = response.data;
      console.log("✅ Society fetched successfully:", data);
      setSocietyData(data || null);
    } catch (err) {
      console.error("❌ Error fetching society:", err);
      setError("Failed to fetch society info");
      setSocietyData(null);
    } finally {
      setLoadingSociety(false);
    }
  };
  useEffect(() => {
    fetchSocietyByAdmin();
  }, []);

  const formatDate = (iso) => {
    if (!iso) return "N/A";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <>
      <Header />
      <div className="d-flex">
        <Sidebar />
        <div className="flex-grow-1 right-section">
          <SubHeader />
          <div className="profile-page-container">
            <div className="profile-content-wrapper">
              {error && (
                <div className="error-banner-premium">
                  <span>{error}</span>
                </div>
              )}

              <div className="profile-cards-grid">
                {/* Society Card */}
                <div className="premium-card profile-society-card-premium">
                  <div className="card-header-gradient">
                    <div className="header-content-left">
                      <div className="header-icon-box">
                        <Building2 size={24} />
                      </div>
                      <div>
                        <h3 className="card-title-premium">Society Information</h3>
                        <p className="card-subtitle-premium">Official details & contact</p>
                      </div>
                    </div>
                    <div className="reg-badge-premium">
                      <span className="reg-label-small">Reg. No</span>
                      <span className="reg-value-premium">
                        {societyData?.society_registration_no ??
                          societyData?.societyRegistrationNo ??
                          getLS("societyRegNo") ??
                          "N/A"}
                      </span>
                    </div>
                  </div>

                  <div className="card-body-premium">
                    {loadingSociety ? (
                      <div className="loading-state-premium">
                        <div className="spinner-premium"></div>
                        <p>Loading society data...</p>
                      </div>
                    ) : (
                      <>
                        <div className="society-name-premium">
                          <Building2 size={20} className="society-icon" />
                          <h4>
                            {societyData?.society ??
                              societyData?.society_name ??
                              userData?.society ??
                              getLS("society") ??
                              "N/A"}
                          </h4>
                        </div>

                        <div className="info-grid-premium">
                          <div className="info-item-premium">
                            <div className="info-icon-wrapper">
                              <Mail size={18} />
                            </div>
                            <div className="info-content">
                              <span className="info-label">Email</span>
                              <span className="info-value">
                                {societyData?.societyEmail ?? userData?.societyEmail ?? "N/A"}
                              </span>
                            </div>
                          </div>

                          <div className="info-item-premium">
                            <div className="info-icon-wrapper">
                              <Phone size={18} />
                            </div>
                            <div className="info-content">
                              <span className="info-label">Contact</span>
                              <span className="info-value">
                                {societyData?.contactPerson ?? userData?.societyPhone ?? "N/A"}
                              </span>
                            </div>
                          </div>

                          <div className="info-item-premium">
                            <div className="info-icon-wrapper">
                              <Users size={18} />
                            </div>
                            <div className="info-content">
                              <span className="info-label">Members</span>
                              <span className="info-value">
                                {String(societyData?.society_member ?? userData?.society_member ?? "N/A")}
                              </span>
                            </div>
                          </div>

                          <div className="info-item-premium full-width">
                            <div className="info-icon-wrapper">
                              <MapPin size={18} />
                            </div>
                            <div className="info-content">
                              <span className="info-label">Address</span>
                              <span className="info-value">
                                {(
                                  [
                                    societyData?.societyAddress,
                                    societyData?.societyAddress2,
                                    societyData?.societyAddress3,
                                    userData?.address
                                  ].filter(Boolean).join(", ") || "N/A"
                                )}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="dates-section-premium">
                          <div className="date-card-premium">
                            <Calendar size={16} />
                            <div>
                              <span className="date-label-premium">Start Date</span>
                              <span className="date-value-premium">
                                {formatDate(societyData?.start_date ?? userData?.start_date)}
                              </span>
                            </div>
                          </div>
                          <div className="date-card-premium">
                            <Calendar size={16} />
                            <div>
                              <span className="date-label-premium">End Date</span>
                              <span className="date-value-premium">
                                {formatDate(societyData?.end_date ?? userData?.end_date)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="action-buttons-premium">
                          <button
                            className="btn-premium btn-primary-premium"
                            type="button"
                            onClick={() => setShowSocietyModal(true)}
                          >
                            <Eye size={18} />
                            View Society ID
                          </button>

                          <button
                            className="btn-premium btn-secondary-premium"
                            type="button"
                            onClick={() => {
                              const sid = getSocietyId();
                              if (!sid || sid === "N/A") {
                                alert("No society id available to copy.");
                                return;
                              }
                              handleCopy(sid);
                            }}
                          >
                            {copied ? (
                              <>
                                <CheckCircle size={18} />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy size={18} />
                                Copy ID
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="card-footer-premium">
                    <small>Use the Society Registration No as the Society ID.</small>
                  </div>
                </div>

                {/* Admin Profile Card */}
                <div className="premium-card profile-admin-card-premium">
                  <div className="card-header-gradient">
                    <div className="header-content-left">
                      <div className="header-icon-box">
                        <User size={24} />
                      </div>
                      <div>
                        <h3 className="card-title-premium">Admin Profile</h3>
                        <p className="card-subtitle-premium">Account information</p>
                      </div>
                    </div>
                  </div>

                  <div className="card-body-premium">
                    {loadingProfile ? (
                      <div className="loading-state-premium">
                        <div className="spinner-premium"></div>
                        <p>Loading profile...</p>
                      </div>
                    ) : userData ? (
                      <>
                        <div className="profile-header-section">
                          <div className="profile-avatar-wrapper">
                            <img
                              src={profileImg}
                              alt="User Avatar"
                              className="profile-avatar-premium"
                            />
                            <div className="avatar-badge-premium">
                              <User size={20} />
                            </div>
                          </div>
                          <div className="profile-identity">
                            <h4 className="admin-name-premium">{userData.admin_name}</h4>
                            <div className="role-badge-premium">
                              {loadingRole ? (
                                <span>Loading role...</span>
                              ) : role ? (
                                <span>{role}</span>
                              ) : (
                                <span>Society Admin</span>
                              )}
                            </div>
                            <p className="society-name-small">{userData.society}</p>
                          </div>
                        </div>

                        <div className="divider-premium"></div>

                        <div className="contact-info-premium">
                          <div className="contact-item-premium">
                            <div className="contact-icon-wrapper">
                              <User size={18} />
                            </div>
                            <div className="contact-details">
                              <span className="contact-label">Full Name</span>
                              <span className="contact-value">{userData.admin_name}</span>
                            </div>
                          </div>

                          <div className="contact-item-premium">
                            <div className="contact-icon-wrapper">
                              <Mail size={18} />
                            </div>
                            <div className="contact-details">
                              <span className="contact-label">Email Address</span>
                              <a
                                href={`mailto:${userData.admin_email}`}
                                className="contact-value contact-link"
                              >
                                {userData.admin_email}
                              </a>
                            </div>
                          </div>

                          <div className="contact-item-premium">
                            <div className="contact-icon-wrapper">
                              <Phone size={18} />
                            </div>
                            <div className="contact-details">
                              <span className="contact-label">Phone Number</span>
                              <a
                                href={`tel:${userData.admin_phone_no}`}
                                className="contact-value contact-link"
                              >
                                {userData.admin_phone_no}
                              </a>
                            </div>
                          </div>

                          {userData.create_password && (
                            <div className="security-badge-premium">
                              <CheckCircle size={16} />
                              <span>Password Configured</span>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      !error && (
                        <div className="empty-state-profile">
                          <User size={48} />
                          <p>No profile found</p>
                        </div>
                      )
                    )}
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Premium Modal */}
          {showSocietyModal && (
            <div className="modal-overlay-premium" onClick={() => setShowSocietyModal(false)}>
              <div className="modal-content-premium" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header-premium">
                  <div className="modal-title-section">
                    <Building2 size={24} className="modal-icon" />
                    <h3>Society ID Information</h3>
                  </div>
                  <button
                    className="modal-close-btn-premium"
                    onClick={() => setShowSocietyModal(false)}
                    aria-label="Close modal"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="modal-body-premium">
                  <div className="modal-info-section">
                    <div className="modal-info-item">
                      <span className="modal-info-label">Society Name</span>
                      <span className="modal-info-value">
                        {societyData?.society ??
                          societyData?.society_name ??
                          userData?.society ??
                          getLS("society") ??
                          "N/A"}
                      </span>
                    </div>

                    <div className="modal-info-item highlight">
                      <span className="modal-info-label">Society ID</span>
                      <code className="society-id-code">{getSocietyId()}</code>
                    </div>
                  </div>

                  <div className="modal-actions-premium">
                    <button
                      className="btn-premium btn-primary-premium"
                      onClick={() => handleCopy(getSocietyId())}
                    >
                      {copied ? (
                        <>
                          <CheckCircle size={18} />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy size={18} />
                          Copy ID
                        </>
                      )}
                    </button>
                    <button
                      className="btn-premium btn-secondary-premium"
                      onClick={() => {
                        const sid = getSocietyId();
                        if (sid && sid !== "N/A") {
                          window.open(`/society/${encodeURIComponent(sid)}`, "_blank");
                        } else {
                          alert("No society id available.");
                        }
                      }}
                    >
                      <Eye size={18} />
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProfilePage;
